import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import api from '../utils/api';
import { toast } from '../hooks/use-toast';
import { 
  CheckCircle, XCircle, AlertTriangle, Send, 
  FileText, User, GraduationCap, Award, BookOpen, 
  Users, Shield, CreditCard, Loader2, ChevronRight,
  PartyPopper, Copy, Download, ExternalLink
} from 'lucide-react';

const ApplicationReview = () => {
  const { applicationId } = useParams();
  const navigate = useNavigate();
  const [review, setReview] = useState(null);
  const [validation, setValidation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [confirmAccuracy, setConfirmAccuracy] = useState(false);
  const [confirmFerpa, setConfirmFerpa] = useState(false);
  const [confirmTerms, setConfirmTerms] = useState(false);
  const [electronicSignature, setElectronicSignature] = useState('');
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [submissionResult, setSubmissionResult] = useState(null);

  useEffect(() => {
    if (applicationId) {
      loadReview();
      loadValidation();
    }
  }, [applicationId]);

  const loadReview = async () => {
    try {
      const response = await api.get(`/review/application/${applicationId}`);
      setReview(response.data);
    } catch (error) {
      // Global handler will show toast
    } finally {
      setLoading(false);
    }
  };

  const loadValidation = async () => {
    try {
      const response = await api.get(`/submission/validate/${applicationId}`);
      setValidation(response.data);
    } catch (error) {
      // If already submitted, validation will fail - that's okay
      console.log('Validation check:', error.response?.data?.detail);
    }
  };

  const handleSubmit = async () => {
    if (!confirmAccuracy || !confirmFerpa || !confirmTerms) {
      toast({ title: 'Error', description: 'Please confirm all checkboxes', variant: 'destructive' });
      return;
    }

    if (!electronicSignature || electronicSignature.trim().length < 2) {
      toast({ title: 'Error', description: 'Please provide your electronic signature', variant: 'destructive' });
      return;
    }

    setSubmitting(true);
    try {
      // Use the comprehensive submission API
      const response = await api.post(
        `/submission/submit/${applicationId}`,
        { 
          confirm_accuracy: true, 
          confirm_ferpa: true,
          confirm_terms: true,
          electronic_signature: electronicSignature.trim()
        }
      );
      
      setSubmissionResult(response.data);
      setShowSuccessDialog(true);
      
      // Also reload review to show updated status
      loadReview();
    } catch (error) {
      toast({ 
        title: 'Cannot Submit', 
        description: error.response?.data?.detail || 'Please complete all required sections', 
        variant: 'destructive' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  const copyConfirmationNumber = () => {
    if (submissionResult?.confirmation_number) {
      navigator.clipboard.writeText(submissionResult.confirmation_number);
      toast({ title: 'Copied!', description: 'Confirmation number copied to clipboard' });
    }
  };

  const getSectionIcon = (sectionId) => {
    const icons = {
      personal_info: User,
      family_info: Users,
      education: GraduationCap,
      courses: BookOpen,
      testing: FileText,
      activities: Award,
      honors: Award,
      essays: FileText,
      supplemental_essays: FileText,
      recommenders: Users,
      ferpa_waiver: Shield,
      payment: CreditCard
    };
    const Icon = icons[sectionId] || FileText;
    return <Icon className="h-5 w-5" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  if (!review) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p>Application not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <Badge className="mb-2">{review.deadline_type} Application</Badge>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Application Review: {review.college?.name}
          </h1>
          <p className="text-gray-600">{review.college?.location}</p>
          {review.college?.deadline && (
            <p className="text-sm text-amber-600 mt-2">
              Deadline: {new Date(review.college.deadline).toLocaleDateString()}
            </p>
          )}
        </div>

        {/* Progress */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-2">
              <span className="font-medium">Application Progress</span>
              <span className="text-emerald-600 font-bold">{review.progress}%</span>
            </div>
            <Progress value={review.progress} className="h-3" />
          </CardContent>
        </Card>

        {/* Status Alert */}
        {review.status === 'submitted' ? (
          <Card className="mb-6 border-green-200 bg-green-50">
            <CardContent className="pt-6 flex items-center gap-3">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div>
                <h3 className="font-semibold text-green-800">Application Submitted</h3>
                <p className="text-sm text-green-600">
                  Submitted on {new Date(review.submitted_at).toLocaleDateString()}
                </p>
              </div>
            </CardContent>
          </Card>
        ) : !review.ready_to_submit ? (
          <Card className="mb-6 border-amber-200 bg-amber-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0" />
                <div>
                  <h3 className="font-semibold text-amber-800">Missing Required Sections</h3>
                  <p className="text-sm text-amber-700 mt-1">
                    Please complete: {review.missing_sections?.join(', ')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {/* Sections Review */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Application Sections</CardTitle>
            <CardDescription>Review each section before submitting</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(review.sections || {}).map(([sectionId, section]) => (
                <div 
                  key={sectionId}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    section.complete ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={section.complete ? 'text-green-600' : 'text-gray-400'}>
                      {getSectionIcon(sectionId)}
                    </div>
                    <div>
                      <p className="font-medium capitalize">
                        {sectionId.replace(/_/g, ' ')}
                      </p>
                      {sectionId === 'essays' && section.data?.word_count && (
                        <p className="text-xs text-gray-500">{section.data.word_count} words</p>
                      )}
                      {sectionId === 'recommenders' && (
                        <p className="text-xs text-gray-500">{section.count} invited</p>
                      )}
                      {sectionId === 'payment' && (
                        <p className="text-xs text-gray-500">
                          {section.status === 'paid' ? `$${section.amount} paid` : section.status}
                        </p>
                      )}
                    </div>
                  </div>
                  <div>
                    {section.complete ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <XCircle className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Validation Warnings */}
        {validation && !validation.ready_to_submit && validation.missing_required?.length > 0 && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardHeader>
              <CardTitle className="text-red-800 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Required Items Missing
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {validation.missing_required.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-red-700">
                    <XCircle className="h-4 w-4" />
                    {item}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Submit Section */}
        {review.status !== 'submitted' && (
          <Card>
            <CardHeader>
              <CardTitle>Submit Application</CardTitle>
              <CardDescription>
                Please review and confirm all statements before final submission
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Confirmation Checkboxes */}
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-start space-x-3">
                  <Checkbox 
                    id="accuracy" 
                    checked={confirmAccuracy}
                    onCheckedChange={setConfirmAccuracy}
                  />
                  <Label htmlFor="accuracy" className="text-sm cursor-pointer">
                    I certify that all information in this application is accurate and complete to the best of my knowledge. I understand that any misrepresentation may result in withdrawal of admission or dismissal.
                  </Label>
                </div>
                
                <div className="flex items-start space-x-3">
                  <Checkbox 
                    id="ferpa" 
                    checked={confirmFerpa}
                    onCheckedChange={setConfirmFerpa}
                  />
                  <Label htmlFor="ferpa" className="text-sm cursor-pointer">
                    I acknowledge that I have read and understand the FERPA waiver statement and have made my decision regarding access to recommendation letters.
                  </Label>
                </div>

                <div className="flex items-start space-x-3">
                  <Checkbox 
                    id="terms" 
                    checked={confirmTerms}
                    onCheckedChange={setConfirmTerms}
                  />
                  <Label htmlFor="terms" className="text-sm cursor-pointer">
                    I agree to the terms and conditions of the Wayo Application Portal and understand that this submission is final and cannot be modified after submission.
                  </Label>
                </div>
              </div>

              {/* Electronic Signature */}
              <div className="space-y-2">
                <Label htmlFor="signature" className="font-medium">
                  Electronic Signature <span className="text-red-500">*</span>
                </Label>
                <p className="text-sm text-gray-500 mb-2">
                  Type your full legal name as your electronic signature
                </p>
                <Input
                  id="signature"
                  placeholder="Enter your full name"
                  value={electronicSignature}
                  onChange={(e) => setElectronicSignature(e.target.value)}
                  className="max-w-md"
                />
              </div>

              <Button 
                onClick={handleSubmit}
                disabled={!review.ready_to_submit || submitting || !confirmAccuracy || !confirmFerpa || !confirmTerms || !electronicSignature.trim()}
                className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
                size="lg"
              >
                {submitting ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Submitting Application...</>
                ) : (
                  <><Send className="h-4 w-4 mr-2" /> Submit Final Application</>
                )}
              </Button>

              {!review.ready_to_submit && (
                <p className="text-sm text-center text-amber-600">
                  Complete all required sections to submit
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {/* Success Dialog */}
        <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <div className="flex items-center justify-center mb-4">
                <div className="bg-green-100 p-4 rounded-full">
                  <PartyPopper className="h-12 w-12 text-green-600" />
                </div>
              </div>
              <DialogTitle className="text-center text-2xl">
                Application Submitted!
              </DialogTitle>
              <DialogDescription className="text-center">
                Your application to {submissionResult?.college_name} has been successfully submitted.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 pt-4">
              {/* Confirmation Number */}
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <p className="text-sm text-emerald-700 mb-1">Confirmation Number</p>
                <div className="flex items-center justify-between">
                  <p className="font-mono font-bold text-lg text-emerald-900">
                    {submissionResult?.confirmation_number}
                  </p>
                  <Button variant="ghost" size="sm" onClick={copyConfirmationNumber}>
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Submission Details */}
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Submitted At</span>
                  <span className="font-medium">
                    {submissionResult?.submitted_at && new Date(submissionResult.submitted_at).toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Deadline Type</span>
                  <Badge>{submissionResult?.deadline_type}</Badge>
                </div>
              </div>

              {/* Next Steps */}
              {submissionResult?.next_steps && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <h4 className="font-semibold text-blue-900 mb-2">Next Steps</h4>
                  <ul className="space-y-1">
                    {submissionResult.next_steps.map((step, idx) => (
                      <li key={idx} className="text-sm text-blue-800 flex items-start gap-2">
                        <ChevronRight className="h-4 w-4 mt-0.5 flex-shrink-0" />
                        {step}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setShowSuccessDialog(false);
                    navigate('/decisions');
                  }}
                >
                  Track Decisions
                </Button>
                <Button 
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => {
                    setShowSuccessDialog(false);
                    navigate('/dashboard');
                  }}
                >
                  Go to Dashboard
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ApplicationReview;
